import { format, formatDistance, parseISO } from 'date-fns'

/**
 * Format currency amount
 */
export function formatCurrency(amount: number | string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num)
}

/**
 * Format date
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return format(dateObj, 'MMM d, yyyy')
}

/**
 * Format datetime
 */
export function formatDateTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date
  return format(dateObj, 'MMM d, yyyy h:mm a')
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  try {
    let dateObj: Date

    if (typeof date === 'string') {
      // Database timestamps are in UTC but don't have timezone indicator
      // Convert MySQL datetime format (YYYY-MM-DD HH:MM:SS) to ISO format with UTC indicator
      let dateString = date

      // If it looks like MySQL datetime format, convert to ISO
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date)) {
        dateString = date.replace(' ', 'T') + 'Z'
      }

      // Try to parse ISO format first
      dateObj = parseISO(dateString)

      // If the parsed date is invalid, try creating a Date directly with UTC
      if (isNaN(dateObj.getTime())) {
        dateObj = new Date(dateString)
      }

      // If still invalid, use current time as fallback
      if (isNaN(dateObj.getTime())) {
        dateObj = new Date()
      }
    } else {
      dateObj = date
    }

    return formatDistance(dateObj, new Date(), { addSuffix: true })
  } catch (error) {
    console.error('Error formatting relative time:', error, 'Date value:', date)
    return 'just now'
  }
}

/**
 * Format number with commas
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num)
}

/**
 * Truncate text
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Capitalize first letter
 */
export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/**
 * Format phone number
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return '(' + match[1] + ') ' + match[2] + '-' + match[3]
  }
  return phone
}
